import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from './config';
import { useTheme } from './metasupply/components/ThemeContext';
import { useResponsive } from './metasupply/components/useResponsive';

interface UserProfile {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  subscription: {
    plan_id: number;
    plan_name: string;
    plan_display_name: string;
    token_limit: number;
    monthly_price_cents: number;
    description: string | null;
  } | null;
  usage: {
    tokens_used: number;
    queries_count: number;
    cost_usd: number;
    usage_percent: number;
  };
  period_start: string;
  created_at: string | null;
}

interface Plan {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  monthly_price_cents: number;
  token_limit: number;
  features: Record<string, any> | null;
  sort_order: number;
}

const formatTokens = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
};

const formatPrice = (cents: number): string => {
  if (cents === 0) return "Free";
  return `$${(cents / 100).toFixed(0)}/mo`;
};

const UsageBar = ({ percent }: { percent: number }) => {
  const { T } = useTheme();
  const barColor = percent >= 90 ? T.danger : percent >= 75 ? T.warning : T.success;
  return (
    <div style={{ width: "100%", background: T.borderLight, borderRadius: 5, height: 10, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(percent, 100)}%`, height: "100%", background: barColor, borderRadius: 5, transition: "width 0.5s ease" }} />
    </div>
  );
};

export default function MyAccount() {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/me/profile`, { credentials: 'include' });
      if (res.ok) setProfile(await res.json());
    } catch (e) {
      console.error('Failed to fetch profile:', e);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/me/plans`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans);
      }
    } catch (e) {
      console.error('Failed to fetch plans:', e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchProfile(), fetchPlans()]);
      setLoading(false);
    };
    load();
  }, [fetchProfile, fetchPlans]);

  const handleUpgrade = async (planId: number) => {
    setUpgrading(planId);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/me/upgrade-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        await fetchProfile();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Upgrade failed' });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Upgrade failed' });
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: T.textLight }}>
        Loading...
      </div>
    );
  }

  const currentPlanId = profile?.subscription?.plan_id;
  const currentSortOrder = plans.find(p => p.id === currentPlanId)?.sort_order ?? -1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>My Account</h2>
        <p style={{ fontSize: 13, color: T.textLight, marginTop: 6, lineHeight: 1.5 }}>View your plan, usage, and available upgrades</p>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: T.radiusSm,
          background: message.type === 'success' ? T.successBg : T.dangerBg,
          color: message.type === 'success' ? T.success : T.danger,
          fontSize: 13,
          fontWeight: 600,
          border: `1px solid ${message.type === 'success' ? '#A7F3D0' : '#FECACA'}`,
        }}>
          {message.text}
        </div>
      )}

      {profile && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Current Plan</div>
            {profile.subscription ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{profile.subscription.plan_display_name}</div>
                <div style={{ fontSize: 14, color: T.textMid, marginTop: 4 }}>{formatPrice(profile.subscription.monthly_price_cents)}</div>
                {profile.subscription.description && (
                  <div style={{ fontSize: 13, color: T.textLight, marginTop: 8 }}>{profile.subscription.description}</div>
                )}
                <div style={{ marginTop: 16, padding: '12px 16px', background: T.offWhite, borderRadius: T.radiusSm }}>
                  <div style={{ fontSize: 12, color: T.textMid, marginBottom: 4 }}>Token Limit</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>{formatTokens(profile.subscription.token_limit)}</div>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, color: T.textLight }}>No plan assigned</div>
            )}
          </div>

          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>This Month's Usage</div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{formatTokens(profile.usage.tokens_used)}</div>
                <div style={{ fontSize: 12, color: T.textLight }}>tokens used</div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{profile.usage.queries_count}</div>
                <div style={{ fontSize: 12, color: T.textLight }}>queries</div>
              </div>
            </div>
            {profile.subscription && (
              <>
                <UsageBar percent={profile.usage.usage_percent} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: T.textLight }}>
                  <span>{profile.usage.usage_percent}% used</span>
                  <span>{formatTokens(profile.subscription.token_limit - profile.usage.tokens_used)} remaining</span>
                </div>
              </>
            )}
            <div style={{ marginTop: 12, fontSize: 12, color: T.textLight }}>
              Billing period: {profile.period_start}
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 16 }}>Available Plans</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {plans.map(plan => {
            const isCurrent = plan.id === currentPlanId;
            const isUpgrade = plan.sort_order > currentSortOrder;
            const isDowngrade = plan.sort_order < currentSortOrder;
            return (
              <div key={plan.id} style={{
                background: T.white,
                border: `2px solid ${isCurrent ? T.gold : T.border}`,
                borderRadius: T.radius,
                padding: 24,
                boxShadow: isCurrent ? T.shadowMd : T.shadow,
                position: 'relative',
              }}>
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: -1, right: 16,
                    background: T.gold, color: T.navy,
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
                    padding: '4px 10px', borderRadius: '0 0 6px 6px',
                  }}>
                    CURRENT
                  </div>
                )}
                <div style={{ fontSize: 18, fontWeight: 800, color: T.navy }}>{plan.display_name}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.gold, marginTop: 8 }}>
                  {plan.monthly_price_cents === 0 ? 'Free' : `$${(plan.monthly_price_cents / 100).toFixed(0)}`}
                  {plan.monthly_price_cents > 0 && <span style={{ fontSize: 14, fontWeight: 500, color: T.textLight }}>/mo</span>}
                </div>
                {plan.description && (
                  <div style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>{plan.description}</div>
                )}
                <div style={{ marginTop: 12, padding: '10px 14px', background: T.offWhite, borderRadius: T.radiusSm }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>{formatTokens(plan.token_limit)}</div>
                  <div style={{ fontSize: 11, color: T.textLight }}>tokens / month</div>
                </div>
                <div style={{ marginTop: 16 }}>
                  {isCurrent ? (
                    <div style={{
                      padding: '10px 16px', borderRadius: T.radiusSm, textAlign: 'center',
                      background: T.offWhite, color: T.textMid, fontSize: 13, fontWeight: 600,
                    }}>
                      Your current plan
                    </div>
                  ) : isUpgrade ? (
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={upgrading === plan.id}
                      style={{
                        width: '100%', padding: '10px 16px', borderRadius: T.radiusSm,
                        border: 'none', background: T.navy, color: T.white,
                        cursor: upgrading === plan.id ? 'not-allowed' : 'pointer',
                        fontSize: 13, fontWeight: 700, opacity: upgrading === plan.id ? 0.7 : 1,
                      }}
                    >
                      {upgrading === plan.id ? 'Upgrading...' : 'Upgrade'}
                    </button>
                  ) : isDowngrade ? (
                    <div style={{
                      padding: '10px 16px', borderRadius: T.radiusSm, textAlign: 'center',
                      background: T.offWhite, color: T.textLight, fontSize: 12, fontWeight: 500,
                    }}>
                      Contact admin to downgrade
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
